import React from "react";
import {
  Card,
  Button,
  Form,
  CardTitle,
  CardText,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import s from "./styles.scss";
import Reform from "@franleplant/reform";
import classNames from "classnames";
import { serviceCaller, ServiceCallerProps } from "../../../Admin/composition";
import Fragment from "../../../toolkit/fragment";
import {
  checkAdminInvite,
  checkForgotPasswordOperation,
  saveForgotPassword,
  saveNewAdminPassword,
  adminOtpChallenge,
} from "../../../utils/fetch";
import AdminOtpPanel from "../../../toolkit/adminOtpPanel";
import { CircularProgress } from "@mui/material";
import IAdminIdentifier from "../../../types/services/IAdminIdentifier";
import { RouterProps, withRouter } from "../../../utils/withRouter";

type DisplayState = "normal" | "success" | "disabledLink";

interface Props extends ServiceCallerProps, RouterProps {
  isForgot: boolean;
}

interface State {
  fields: {
    newPassword: string;
    newPasswordConfirm: string;
  };
  isLoading: boolean;
  forgotError: boolean;
  displayState: DisplayState;
  adminIdentifier: IAdminIdentifier;
  recoveryStep: null | "otp" | "password";
  recoveryChallengeId: string | null;
  recoveryToken: string | null;
  recoveryOtpSmsEligible: boolean;
  recoveryOtpDeliveryChannel: "email" | "sms";
}

class InvitePage extends React.Component<Props, State> {
  re = Reform.reactMixins.objectMixin(this);

  state = {
    fields: {
      newPassword: "",
      newPasswordConfirm: "",
    },
    errors: {},
    isLoading: true,
    forgotError: false,
    displayState: "normal" as DisplayState,
    adminIdentifier: null,
    recoveryStep: null,
    recoveryChallengeId: null,
    recoveryToken: null,
    recoveryOtpSmsEligible: false,
    recoveryOtpDeliveryChannel: "email",
  };

  validationRules = {
    newPassword: { required: true, minLength: 6 },
    newPasswordConfirm: {
      required: true,
      minLength: 6,
      equal: val => {
        return val !== this.state.fields.newPassword;
      },
    },
  };

  validationMessages = {
    required: _ => `Required`,
    minLength: ruleValue => `Must be at least ${ruleValue} characters long`,
    equal: () => `Password must match`,
    default: _ => `Invalid`,
  };

  componentDidMount() {
    if (this.props.config) {
      this.checkForInvite();
    }
  }

  componentDidUpdate() {
    if (
      this.props.config &&
      this.state.isLoading &&
      this.state.adminIdentifier === null
    ) {
      this.checkForInvite();
    }
  }

  onChangeFactory = fieldName => {
    return event => {
      const value = event.target.value;
      this.setState(state => {
        const fields = {
          ...state.fields,
          [fieldName]: value,
        };

        return { ...state, error: {}, fields };
      });
    };
  };

  checkForInvite = () => {
    let call = this.props.isForgot
      ? checkForgotPasswordOperation(
          this.props.config.authServiceUrl,
          this.props.params.operationId
        )
      : checkAdminInvite(
          this.props.config.authServiceUrl,
          this.props.params.operationId
        );

    call
      .then(response => {
        return adminOtpChallenge(this.props.config.authServiceUrl, {
          clientType: "web",
          operation: this.props.isForgot ? "forgot" : "invite",
          operationId: this.props.params.operationId,
        }).then(ch => ({ response, ch }));
      })
      .then(({ response, ch }) => {
        if (!ch.otpRequired && ch.completionToken) {
          this.setState({
            isLoading: false,
            adminIdentifier: response.element,
            recoveryStep: "password",
            recoveryToken: ch.completionToken,
          });
          return;
        }
        if (ch.challengeId) {
          this.setState({
            isLoading: false,
            adminIdentifier: response.element,
            recoveryStep: "otp",
            recoveryChallengeId: ch.challengeId,
            recoveryOtpSmsEligible: ch.deliveryChannel === "sms",
            recoveryOtpDeliveryChannel: ch.deliveryChannel === "sms" ? "sms" : "email",
          });
          return;
        }
        this.setState({
          displayState: "disabledLink",
          isLoading: false,
        });
      })
      .catch(_ => {
        this.setState({
          displayState: "disabledLink",
          isLoading: false,
        });
      });
  };

  onSubmit = e => {
    e.preventDefault();
    const isValid = this.re.validateFormFromState();
    if (!isValid) {
      return;
    }
    this.setState(
      {
        isLoading: true,
      },
      () => {
        let call = this.props.isForgot
          ? saveForgotPassword(this.props.config.authServiceUrl, {
              operationId: this.props.params.operationId,
              password: this.state.fields.newPassword,
              completionToken: this.state.recoveryToken || undefined,
            })
          : saveNewAdminPassword(this.props.config.authServiceUrl, {
              operationId: this.props.params.operationId,
              password: this.state.fields.newPassword,
              completionToken: this.state.recoveryToken || undefined,
            });

        call
          .then(response => {
            if (response === "Success") {
              this.setState({
                isLoading: false,
                displayState: "success",
              });
            } else {
              this.setState({
                displayState: "disabledLink",
                isLoading: false,
              });
            }
          })
          .catch(() => {
            this.setState({
              isLoading: false,
              displayState: "disabledLink",
            });
          });
      }
    );
  };

  onInviteOtpVerified = (completionToken: string) => {
    this.setState({
      recoveryStep: "password",
      recoveryToken: completionToken,
    });
  };

  getTitle = () => {
    if (this.state.displayState === "normal") {
      return this.props.isForgot ? "Reset Password" : "Welcome to ViewSpark";
    } else if (this.state.displayState === "success") {
      return "Password Saved";
    } else {
      return "Sorry!";
    }
  };

  render() {
    const skipCardTextSpacer =
      this.state.displayState === "normal" &&
      this.props.isForgot &&
      (this.state.recoveryStep === "otp" ||
        this.state.recoveryStep === "password");

    return (
      <div className={s.popup}>
        <Card
          body
          className={
            s.roundedCorners +
            " " +
            (this.state.displayState !== "normal" ? s.disabled : "")
          }
        >
          <CardTitle
            className={skipCardTextSpacer ? s.cardTitleForgotFlowTight : undefined}
          >
            <p
              className={classNames(
                s.title,
                skipCardTextSpacer && s.titleForgotFlowTight
              )}
            >
              {this.getTitle()}
            </p>
          </CardTitle>
          {!skipCardTextSpacer ? (
            <>
          <br />
          <CardText className={s.text}>
            {this.state.displayState === "normal" && (
              <Fragment>
                {this.state.recoveryStep == null && this.state.isLoading ? (
                  <div className={s.verifyingInline}>
                    <CircularProgress size={40} thickness={4} className={s.verifyingSpinner} aria-hidden />
                    <span>Verifying link and preparing secure verification…</span>
                  </div>
                ) : (
                  <>
                {!this.props.isForgot
                  ? "To finish your registration, complete the following information:"
                  : null}
                  </>
                )}
              </Fragment>
            )}
            {this.state.displayState === "success" && (
              <Fragment>
                {this.props.isForgot
                  ? "Reset finished, please go to sign in"
                  : "Registration finished, please go to sign in."}
              </Fragment>
            )}
            {this.state.displayState === "disabledLink" && (
              <Fragment>
                This link has expired. <br />
                {this.props.isForgot
                  ? "Please reset your password again."
                  : "Please contact an admin user."}
              </Fragment>
            )}
          </CardText>
            </>
          ) : null}
            {this.state.displayState === "normal" &&
              this.state.recoveryStep === "otp" &&
              this.props.config?.authServiceUrl &&
              this.state.recoveryChallengeId && (
                <AdminOtpPanel
                  authServiceUrl={this.props.config.authServiceUrl}
                  challengeId={this.state.recoveryChallengeId}
                  smsEligible={this.state.recoveryOtpSmsEligible}
                  initialChannel={this.state.recoveryOtpDeliveryChannel}
                  onVerified={this.onInviteOtpVerified}
                />
              )}
            {this.state.displayState === "normal" &&
              this.state.recoveryStep === "password" && (
            <Form className={s.paddingForm}>
              <FormGroup>
                <Label for="newPassword" className={s.label}>
                  Password
                </Label>
                <Input
                  type="password"
                  name="new Password"
                  id="newPassword"
                  value={this.state.fields.newPassword}
                  onChange={this.onChangeFactory("newPassword")}
                  className={classNames(s.textField, s.roundedCorners)}
                />
              </FormGroup>
              <ul>
                {this.re.mapFieldErrors("newPassword").map((message, index) => {
                  return (
                    <li key={index} className={s.error}>
                      {message}
                    </li>
                  );
                })}
              </ul>
              <FormGroup>
                <Label for="confirmPassword" className={s.label}>
                  Confirm Password
                </Label>
                <Input
                  type="password"
                  name="confirm Password"
                  id="confirmPassword"
                  value={this.state.fields.newPasswordConfirm}
                  onChange={this.onChangeFactory("newPasswordConfirm")}
                  className={classNames(s.textField, s.roundedCorners)}
                />
              </FormGroup>
              <ul>
                {this.re
                  .mapFieldErrors("newPasswordConfirm")
                  .map((message, index) => {
                    return (
                      <li key={index} className={s.error}>
                        {message}
                      </li>
                    );
                  })}
              </ul>
              <div className={s.centerDiv}>
                <Button
                  id={"savePasswordButton"}
                  onClick={this.onSubmit}
                  className={classNames(s.button, s.roundedCorners)}
                  disabled={
                    this.state.isLoading ||
                    this.state.fields.newPassword !==
                      this.state.fields.newPasswordConfirm
                  }
                  color={"none"}
                >
                  Save Password
                </Button>
              </div>
            </Form>
              )}
        </Card>
        <br />
        {this.state.displayState === "success" && (
          <div className={s.centerDiv}>
            <Button
              id={"returnToSignIn"}
              color="link"
              className={s.link}
              onClick={() => this.props.navigate("/")}
            >
              Go to Sign In
            </Button>
          </div>
        )}
      </div>
    );
  }
}

export default serviceCaller(withStyles(s)(withRouter(InvitePage)));
